import React, { useState, useContext, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import EkonomiskAnalysQuery from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import AvailableSumCategoriesQuery from '../SideMenu/EkonomiskAnalysSummeringSelect/Queries/AvailableSumCategoriesQuery';
import { kontodelarSumsFromCategories } from '../../Utils/convertUtils';
import EkonomiskAnalysLopandeTable from '../EkonomiskAnalysLopandeTable/EkonomiskAnalysLopandeTable';
import EkonomiskAnalysRadBaseradUrvalQuery from '../../Queries/EkonomiskAnalysRadBaseradUrvalQuery/EkonomiskAnalysRadBaseradUrvalQuery';
import {
  convertUIUrvalToTEDUrval,
  tryConvertUIUrvalToTEDUrval,
} from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { getEmptyUrval } from '../../../../../../../Components/Urval/Utils/urvalUtils';
import { ISumCategory } from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import EkonomiskAnalysSummeringSelect from '../SideMenu/EkonomiskAnalysSummeringSelect/EkonomiskAnalysSummeringSelect';
import { getForceHelarsJamforelse } from '../../Utils/userInfoUtils';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import EkonomiskAnalysExcelModal from '../EkonomiskAnalysExcelModal/EkonomiskAnalysExcelModal';
import EnhancedBokforingsarActions from '../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import FunctionMenu from '../../../../../_Common/Components/FunctionMenu/FunctionMenu/FunctionMenu';
import FunctionItem from '../../../../../_Common/Components/FunctionMenu/FunctionItem/FunctionItem';
import {
  defaultBudgetSummationMode,
  defaultSummationMode,
} from '../../Utils/SummationModeUtils';
import { DateInputType } from '../../../../../../../Utils/dateUtils';

interface IProps {
  settings: IFilledLocalAnalys;
  updatedSettings: ILocalAnalys;
  urvalId: string;
  selectedAnalys: ISelectedAnalys;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  rowName?: string | undefined;
}

interface IInnerProps extends IProps {
  availableCategories: ISumCategory[] | undefined;
}

const useStyles = makeStyles()(() => ({
  tableContainer: {
    position: 'relative',
  },

  menu: {
    position: 'absolute',
    right: 0,
    zIndex: 200,
  },
}));

const EkonomiskAnalysRadBaseradGranskning = (props: IInnerProps) => {
  const { classes } = useStyles();
  const ekonomiskAnalysContext = useContext(EkonomiskAnalysContext);
  const [showExcelModal, setShowExcelModal] = useState(false);

  const [selectedSums, setSelectedSums] = useState(
    kontodelarSumsFromCategories(props.availableCategories)
  );

  const toggleExcelModal = useCallback(() => {
    setShowExcelModal(!showExcelModal);
  }, [showExcelModal]);

  const analysSettings: IFilledLocalAnalys = {
    anvandsFor: [],
    columns: props.settings.columns,
    dateInputType: DateInputType.yearMonthDay,
    dates: props.settings.dates,
    defaultSettings: {
      budgetSummationMode: defaultBudgetSummationMode,
      decimals: 1,
      denomination: 1,
      exkluderaPersonalKostnader: false,
      includeEjBokfordaJournaler: false,
      showComparison: false,
      summationMode: defaultSummationMode,
      visaSummeringar: true,
    },
    expandToIndex: 0,
    isInterval: false,
    loading: false,
    lopsaldoDetaljrad: false,
    ai_prompt: null,
    detaljanalys: false,
    name: props.selectedAnalys.analys.id,
    other: props.settings.other,
    sparatAnvandarUrval: null,
    sums: selectedSums,
    type: 'RAPPORTDIMENSIONLOPANDE',
    urval: getEmptyUrval(),
    visaSummeringar: props.settings.visaSummeringar,
  };

  return (
    <EkonomiskAnalysRadBaseradUrvalQuery urvalId={props.urvalId}>
      {rowDetaljUrval => {
        return (
          <EkonomiskAnalysQuery
            name={props.settings.name}
            title={props.settings.title}
            category={props.settings.category}
            columns={props.settings.columns.map(x => x.data)}
            sums={selectedSums}
            dates={props.settings.dates}
            codePrivilegesId={
              props.impersonatedCodePrivilege
                ? props.impersonatedCodePrivilege.id
                : props.selectedAnalys.codePrivilege.id
            }
            baseUrvalId={
              props.selectedAnalys.analys.grundurval
                ? props.selectedAnalys.analys.grundurval.id
                : undefined
            }
            endastBokfort={!props.settings.other.includeEjBokfordaJournaler}
            forceHelarsjamforelse={getForceHelarsJamforelse(
              ekonomiskAnalysContext.harPeriodiseradBudget,
              ekonomiskAnalysContext.userInfo?.preferHelarsjamforelse
            )}
            detailedSelection={convertUIUrvalToTEDUrval(rowDetaljUrval)}
            settingsChanged={false}
            setRefetchAnalys={undefined}
            urval={tryConvertUIUrvalToTEDUrval(props.settings.urval)}
            visaSummeringar={props.settings.visaSummeringar}
          >
            {data => (
              <div className={classes.tableContainer}>
                <span className={classes.menu}>
                  <FunctionMenu id="ekonomiskAnalys">
                    <FunctionItem
                      iconType="excel_export"
                      onClick={toggleExcelModal}
                    >
                      Exportera till Excel
                    </FunctionItem>
                  </FunctionMenu>
                </span>
                <EkonomiskAnalysLopandeTable
                  columns={data.columns}
                  rows={data.data}
                  impersonatedCodePrivilege={props.impersonatedCodePrivilege}
                  updateImpersonatedCodePrivilege={
                    props.updateImpersonatedCodePrivilege
                  }
                  showComparisonNumber={false}
                  analysSettings={analysSettings}
                  updatedAnalysSettings={props.updatedSettings}
                  updatedAnalysSettingsUrval={props.updatedSettings.urval}
                  totalSumRow={data.totalSum}
                  onDenominationChange={() => {}}
                  expandedLevel={0}
                  selectedAnalys={props.selectedAnalys}
                  tableHeaderLeftArea={
                    <EkonomiskAnalysSummeringSelect
                      selectedSums={selectedSums}
                      onUpdate={newSums => {
                        setSelectedSums(newSums);
                      }}
                      buttonGutter
                    />
                  }
                  isSubTable
                  detailedSelection={convertUIUrvalToTEDUrval(rowDetaljUrval)}
                />
                {props.selectedAnalys && (
                  <EnhancedBokforingsarActions>
                    {({ isPeriodLocked }) => (
                      <EkonomiskAnalysExcelModal
                        analysSettings={{
                          ...analysSettings,
                          loading: props.settings.loading,
                          name: props.rowName || props.settings.name,
                          urval: rowDetaljUrval,
                        }}
                        forceHelarsjamforelse={getForceHelarsJamforelse(
                          ekonomiskAnalysContext.harPeriodiseradBudget,
                          ekonomiskAnalysContext.userInfo
                            ?.preferHelarsjamforelse
                        )}
                        impersonatedCodePrivilege={
                          props.impersonatedCodePrivilege
                        }
                        isPeriodLocked={isPeriodLocked || (() => true)}
                        onClose={toggleExcelModal}
                        open={showExcelModal}
                        radbaserad={false}
                        selectedAnalys={props.selectedAnalys}
                      />
                    )}
                  </EnhancedBokforingsarActions>
                )}
              </div>
            )}
          </EkonomiskAnalysQuery>
        );
      }}
    </EkonomiskAnalysRadBaseradUrvalQuery>
  );
};

const EnhancedEkonomiskAnalysRadBaseradGranskning: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  return (
    <AvailableSumCategoriesQuery>
      {({ availableCategories }) => (
        <EkonomiskAnalysRadBaseradGranskning
          {...props}
          availableCategories={availableCategories}
        />
      )}
    </AvailableSumCategoriesQuery>
  );
};

export default EnhancedEkonomiskAnalysRadBaseradGranskning;
